import type { Node } from "prosemirror-model";
import { NodeSelection, type Transaction } from "prosemirror-state";

export type DropSide = "before" | "after";

interface Box {
	left: number;
	top: number;
	width: number;
	height: number;
}

const MERGE_ZONE_EDGE = 0.25;

export const getDropSide = ({ left, top, width, height }: Box, x: number, y: number): DropSide =>
	(x - left) / width + (y - top) / height < 1 ? "before" : "after";

export const isMergeZone = ({ top, height }: Pick<Box, "top" | "height">, y: number) => {
	const offset = (y - top) / height;
	return offset >= MERGE_ZONE_EDGE && offset <= 1 - MERGE_ZONE_EDGE;
};

const childStart = (gallery: Node, galleryPos: number, index: number) => {
	let pos = galleryPos + 1;
	for (let child = 0; child < index; child++) pos += gallery.child(child).nodeSize;
	return pos;
};

const removeSource = (tr: Transaction, sourcePos: number, item: Node) => {
	const Source = tr.doc.resolve(sourcePos);
	const isLastInGallery = Source.parent.type.name === "gallery" && Source.parent.childCount === 1;
	if (isLastInGallery) tr.delete(Source.before(), Source.after());
	else tr.delete(sourcePos, sourcePos + item.nodeSize);
};

export const dropIntoGallery = (
	tr: Transaction,
	galleryPos: number,
	insertIndex: number,
	item: Node,
	sourcePos: number | null,
) => {
	let insertPos = childStart(tr.doc.nodeAt(galleryPos), galleryPos, insertIndex);

	if (sourcePos !== null) {
		removeSource(tr, sourcePos, item);
		insertPos = tr.mapping.map(insertPos);
	}

	tr.insert(insertPos, item);
	tr.setSelection(NodeSelection.create(tr.doc, insertPos));
	return tr.setMeta("uiEvent", "drop");
};

export const mergeIntoGallery = (
	tr: Transaction,
	targetPos: number,
	side: DropSide,
	item: Node,
	sourcePos: number | null,
) => {
	let pos = targetPos;

	if (sourcePos !== null) {
		removeSource(tr, sourcePos, item);
		pos = tr.mapping.map(targetPos);
	}

	const target = tr.doc.nodeAt(pos);
	const items = side === "before" ? [item, target] : [target, item];
	tr.replaceWith(pos, pos + target.nodeSize, tr.doc.type.schema.nodes.gallery.create(null, items));
	tr.setSelection(NodeSelection.create(tr.doc, side === "before" ? pos + 1 : pos + 1 + target.nodeSize));
	return tr.setMeta("uiEvent", "drop");
};
